// Direct-booking pipeline widget — added to Owner BI page.
// Shows coupon issuance → redemption conversion + estimated OTA commission saved.
import React, { useCallback, useEffect, useState } from 'react';
import { Gift, Loader2, TrendingUp } from 'lucide-react';
import { apiCall } from './_shared';

const eur = (n) => `€${Math.round(Number(n || 0)).toLocaleString('en-EU')}`;

const CouponPipelineWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const json = await apiCall('/api/admin/coupons/analytics?days=90');
      setData(json);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = data?.counts || { issued: 0, active: 0, redeemed: 0, expired: 0 };
  const conv = data?.conversion_pct ?? 0;
  const saved = data?.estimated_commission_saved_eur ?? 0;

  // Funnel bar segments.
  const total = Math.max(1, counts.issued);
  const segRedeemed = (counts.redeemed / total) * 100;
  const segActive = (counts.active / total) * 100;
  const segExpired = (counts.expired / total) * 100;

  return (
    <div className="bg-white rounded-xl border p-5" data-testid="coupon-pipeline-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Gift className="w-5 h-5 text-orange-500" /> Direct-booking pipeline
        </h3>
        <span className="text-xs text-gray-400">last 90 days</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        </div>
      ) : err ? (
        <p className="text-sm text-red-600">{err}</p>
      ) : (
        <>
          {/* Headline stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Issued</p>
              <p className="text-2xl font-bold text-gray-900">{counts.issued}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Conversion</p>
              <p className="text-2xl font-bold text-orange-600">{conv}%</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-green-500" /> Saved
              </p>
              <p className="text-2xl font-bold text-green-600" data-testid="coupon-saved">{eur(saved)}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">est. OTA commission</p>
            </div>
          </div>

          {/* Funnel */}
          <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100 mb-2" title={`${counts.issued} issued`}>
            {segRedeemed > 0 && <div className="bg-green-500" style={{ width: `${segRedeemed}%` }} title={`${counts.redeemed} redeemed`} />}
            {segActive > 0 && <div className="bg-orange-400" style={{ width: `${segActive}%` }} title={`${counts.active} still active`} />}
            {segExpired > 0 && <div className="bg-gray-300" style={{ width: `${segExpired}%` }} title={`${counts.expired} expired`} />}
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-gray-600">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-full" /> {counts.redeemed} redeemed</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-orange-400 rounded-full" /> {counts.active} active</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-gray-300 rounded-full" /> {counts.expired} expired</span>
          </div>

          <p className="text-[11px] text-gray-400 mt-3 italic">
            Estimate uses avg booking €{data?.avg_booking_value_eur} × {Math.round((data?.commission_rate || 0) * 100)}% OTA commission.
          </p>
        </>
      )}
    </div>
  );
};

export default CouponPipelineWidget;
